import React, { useState, useCallback, useEffect } from 'react';
import { Layout, Typography, Button, Space, Card, Tag, notification, message } from 'antd';
import {
  DownloadOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
  ClearOutlined
} from '@ant-design/icons';

import { FileUpload } from './FileUpload';
import { FileList } from './FileList';
import { ProgressBar } from './ProgressBar';
import { ImagePreview } from './ImagePreview';
import { BatchProcessorProvider, useBatchProcessor } from '../services/BatchProcessor';
import { FileInfo, ProcessedImage, CONSTANTS } from '../types';
import { PerformanceUtils } from '../utils/performanceUtils';
import { FileUtils } from '../utils/fileUtils';

import './App.css';
import 'antd/dist/reset.css';

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;

// 主应用组件
const AppContent: React.FC = () => {
  const {
    files,
    processedImages,
    isProcessing,
    isPaused,
    currentProgress,
    batchResult,
    selectedFileIds,
    addFiles,
    removeFile,
    startProcessing,
    pauseProcessing,
    resumeProcessing,
    stopProcessing,
    selectFiles,
    clearAll,
    // downloadSingle,
    downloadBatch,
    setEventHandlers
  } = useBatchProcessor();

  const [previewImage, setPreviewImage] = useState<ProcessedImage | FileInfo | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // 设置事件处理器
  useEffect(() => {
    setEventHandlers({
      onFileAdded: (file) => {
        message.success(`已添加文件: ${file.name}`);
      },
      onFileRemoved: (fileId) => {
        const file = files.find(f => f.id === fileId);
        if (file) {
          message.info(`已删除文件: ${file.name}`);
        }
      },
      onProcessingStart: (fileIds) => {
        notification.info({
          message: '开始处理',
          description: `正在处理 ${fileIds.length} 个文件`,
          duration: 2
        });
      },
      onProcessingComplete: (result) => {
        const { successCount, errorCount, totalFiles } = result;

        if (errorCount === 0) {
          notification.success({
            message: '处理完成',
            description: `成功处理 ${successCount} 个文件`,
            duration: 4
          });
        } else {
          notification.warning({
            message: '处理完成',
            description: `成功: ${successCount}, 失败: ${errorCount}, 总计: ${totalFiles}`,
            duration: 6
          });
        }
      },
      onProcessingError: (error, fileId) => {
        const file = files.find(f => f.id === fileId);
        notification.error({
          message: '处理失败',
          description: `文件 "${file?.name}" 处理失败: ${error.message}`,
          duration: 5
        });
      },
      onDownloadStart: (fileIds) => {
        message.loading({
          content: `正在准备下载 ${fileIds.length} 个文件...`,
          key: 'download',
          duration: 0
        });
      },
      onDownloadComplete: (count) => {
        message.success({
          content: `已下载 ${count} 个文件`,
          key: 'download',
          duration: 3
        });
      }
    });
  }, [files, setEventHandlers]);

  // 启动内存监控
  useEffect(() => {
    PerformanceUtils.startMemoryMonitoring();

    // 添加内存警告回调
    const removeCallback = PerformanceUtils.addMemoryWarningCallback((memoryInfo) => {
      if (memoryInfo.memoryUsagePercentage > 0.9) {
        notification.warning({
          message: '内存使用过高',
          description: `当前内存使用率: ${(memoryInfo.memoryUsagePercentage * 100).toFixed(1)}%，建议减少处理文件数量`,
          duration: 10
        });
      }
    });

    return () => {
      PerformanceUtils.stopMemoryMonitoring();
      removeCallback();
    };
  }, []);

  // 文件上传处理
  const handleFilesAdded = useCallback(async (newFiles: FileInfo[]) => {
    addFiles(newFiles);
  }, [addFiles]);

  // 文件预览
  const handleFilePreview = useCallback((fileId: string) => {
    const file = files.find(f => f.id === fileId);
    const processedImage = processedImages.find(p => p.id === fileId);

    if (processedImage) {
      setPreviewImage(processedImage);
    } else if (file) {
      setPreviewImage(file);
    }

    setShowPreview(true);
  }, [files, processedImages]);

  // 开始处理
  const handleStartProcessing = useCallback(() => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) {
      message.warning('没有待处理的文件');
      return;
    }
    startProcessing();
  }, [files, startProcessing]);

  // 批量下载
  const handleBatchDownload = useCallback(async () => {
    const completedImages = processedImages.filter(img =>
      selectedFileIds.length === 0 || selectedFileIds.includes(img.id)
    );

    if (completedImages.length === 0) {
      message.warning('没有可下载的文件');
      return;
    }

    try {
      await downloadBatch(completedImages.map(img => img.id));
    } catch (error) {
      notification.error({
        message: '下载失败',
        description: error instanceof Error ? error.message : '下载时发生未知错误',
        duration: 5
      });
    }
  }, [processedImages, selectedFileIds, downloadBatch]);

  // 计算统计信息
  const getStats = () => {
    const total = files.length;
    const pending = files.filter(f => f.status === 'pending').length;
    const processing = files.filter(f => f.status === 'processing').length;
    const completed = files.filter(f => f.status === 'completed').length;
    const errors = files.filter(f => f.status === 'error').length;

    return { total, pending, processing, completed, errors };
  };

  const stats = getStats();

  return (
    <Layout className="app-layout">
      <Header className="app-header">
        <div className="header-content">
          <div className="header-title">
            <Title level={2} style={{ margin: 0, color: '#fff', textAlign: 'center' }}>
              🎨 白底横版图片生成器
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', textAlign: 'center', display: 'block' }}>
              一键将竖版图片转换为1920×1080横版白底图片
            </Text>
          </div>
          <div className="header-badge">
            <Tag
              color="gold"
              style={{
                fontSize: '12px',
                fontWeight: 'bold',
                border: 'none',
                color: '#722ed1'
              }}
            >
              文创团队专用
            </Tag>
          </div>
        </div>
      </Header>

      <Content className="app-content">
        <div className="content-container">
          {/* 文件上传区域 */}
          <Card title="📤 上传图片" className="upload-section">
            <FileUpload
              onFilesAdded={handleFilesAdded}
              maxFileSize={CONSTANTS.MAX_FILE_SIZE}
              maxFiles={CONSTANTS.MAX_BATCH_SIZE}
              acceptedFormats={CONSTANTS.SUPPORTED_FORMATS}
              disabled={isProcessing}
            />
          </Card>

          {/* 操作控制栏 */}
          {files.length > 0 && (
            <Card size="small" className="control-section">
              <div className="control-content">
                <div className="stats-info">
                  <Space size="large">
                    <Text>总计: <Text strong>{stats.total}</Text></Text>
                    <Text>待处理: <Text strong>{stats.pending}</Text></Text>
                    <Text>已完成: <Text strong style={{ color: '#52c41a' }}>{stats.completed}</Text></Text>
                    {stats.errors > 0 && (
                      <Text>失败: <Text strong style={{ color: '#f5222d' }}>{stats.errors}</Text></Text>
                    )}
                  </Space>
                </div>

                <div className="control-actions">
                  <Space>
                    {!isProcessing && stats.pending > 0 && (
                      <Button
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        onClick={handleStartProcessing}
                        size="large"
                      >
                        开始处理
                      </Button>
                    )}

                    {isProcessing && !isPaused && (
                      <Button
                        icon={<PauseCircleOutlined />}
                        onClick={pauseProcessing}
                      >
                        暂停
                      </Button>
                    )}

                    {isProcessing && isPaused && (
                      <Button
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        onClick={resumeProcessing}
                      >
                        继续
                      </Button>
                    )}

                    {isProcessing && (
                      <Button
                        danger
                        icon={<StopOutlined />}
                        onClick={stopProcessing}
                      >
                        停止
                      </Button>
                    )}

                    {stats.completed > 0 && (
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={handleBatchDownload}
                        ghost
                      >
                        批量下载 ({stats.completed})
                      </Button>
                    )}

                    <Button
                      icon={<ClearOutlined />}
                      onClick={clearAll}
                      disabled={isProcessing}
                    >
                      清空
                    </Button>
                  </Space>
                </div>
              </div>
            </Card>
          )}

          {/* 进度显示 */}
          {currentProgress && (
            <ProgressBar
              progress={currentProgress}
              showDetail={true}
              compact={false}
            />
          )}

          {/* 文件列表 */}
          {files.length > 0 && (
            <Card title="📋 文件列表" className="file-list-section">
              <FileList
                files={files}
                onFileRemove={removeFile}
                onFilePreview={handleFilePreview}
                onFilesSelect={selectFiles}
                selectedFileIds={selectedFileIds}
              />
            </Card>
          )}

          {/* 处理结果汇总 */}
          {batchResult && (
            <Card title="📊 处理结果" className="result-section">
              <div className="result-summary">
                <Space size="large">
                  <div className="result-item">
                    <Text type="secondary">总文件数</Text>
                    <Title level={3} style={{ margin: 0 }}>{batchResult.totalFiles}</Title>
                  </div>
                  <div className="result-item">
                    <Text type="secondary">成功处理</Text>
                    <Title level={3} style={{ margin: 0, color: '#52c41a' }}>{batchResult.successCount}</Title>
                  </div>
                  <div className="result-item">
                    <Text type="secondary">处理失败</Text>
                    <Title level={3} style={{ margin: 0, color: '#f5222d' }}>{batchResult.errorCount}</Title>
                  </div>
                  <div className="result-item">
                    <Text type="secondary">总耗时</Text>
                    <Title level={3} style={{ margin: 0 }}>
                      {PerformanceUtils.formatDuration(batchResult.totalProcessingTime || 0)}
                    </Title>
                  </div>
                </Space>
              </div>
            </Card>
          )}
        </div>
      </Content>

      <Footer className="app-footer">
        <div className="footer-content">
          <Text type="secondary">
            © 2025 白底图片生成工具 |
            支持格式: JPG、PNG、WEBP |
            单文件最大: {FileUtils.formatFileSize(CONSTANTS.MAX_FILE_SIZE)} |
            批量最多: {CONSTANTS.MAX_BATCH_SIZE} 个文件
          </Text>
        </div>
      </Footer>

      {/* 图片预览模态框 */}
      {previewImage && (
        <ImagePreview
          image={previewImage}
          visible={showPreview}
          onClose={() => setShowPreview(false)}
          showComparison={previewImage && 'processedBlob' in previewImage}
        />
      )}
    </Layout>
  );
};

// 带Provider的主应用
const App: React.FC = () => {
  return (
    <BatchProcessorProvider>
      <AppContent />
    </BatchProcessorProvider>
  );
};

export default App;